import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  type ReactNode,
} from 'react'
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth'
import type {
  AuthContextType,
  User,
  LoginCredentials,
  AuthResponse,
  AuthState,
  UserRole,
  PermissionResource,
  PermissionAction,
  UserPermissions,
} from '../types'
import {
  createUserPermissions,
  extractRoleFromCognitoGroups,
} from '../utils/permissions'
import '../config/amplify'

// Auth reducer actions
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SIGN_OUT' }
  | { type: 'SET_PERMISSIONS'; payload: UserPermissions | null }

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null,
      }
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    case 'SET_PERMISSIONS':
      return {
        ...state,
        userPermissions: action.payload,
      }
    case 'SIGN_OUT':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        userPermissions: null,
      }
    default:
      return state
  }
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  userPermissions: null,
}

// Create context
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

// AuthProvider component
interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Generate user object from Cognito session
  const generateUser = async (): Promise<User | null> => {
    try {
      const session = await fetchAuthSession()
      const attributes = session?.tokens?.idToken?.payload

      if (!attributes || !session?.tokens?.accessToken) {
        return null
      }

      // Extract Cognito groups from the token
      const cognitoGroups = (attributes['cognito:groups'] as string[]) || []

      // Extract user role from Cognito groups
      const userRole = extractRoleFromCognitoGroups(cognitoGroups)

      const user: User = {
        id: attributes.sub as string,
        email: attributes.email as string,
        name: attributes.name as string,
        profile: attributes.profile as string,
        role: userRole || undefined,
        cognito_groups: cognitoGroups,
      }

      // Generate nickname and avatar
      user.nickname = user.name?.split(' ')[0] || 'Usuario'
      user.avatar = user.name
        ?.split(' ')
        .map(n => n[0])
        .join('')
      user.token = session.tokens.accessToken.toString()

      return user
    } catch (error: any) {
      // Don't log expected authentication errors
      if (
        error?.name !== 'UserUnAuthenticatedException' &&
        error?.name !== 'NotAuthorizedException'
      ) {
        console.error('Error generating user:', error)
      }
      return null
    }
  }

  // Get current user
  const getCurrentUser = async (): Promise<User | null> => {
    try {
      const cognitoUser = await amplifyGetCurrentUser()
      if (!cognitoUser) {
        return null
      }

      const user = await generateUser()
      return user
    } catch (error: any) {
      // Don't log expected authentication errors
      if (
        error.name === 'UserUnAuthenticatedException' ||
        error.name === 'NotAuthorizedException'
      ) {
        return null
      }

      // Log unexpected errors
      console.error('Error getting current user:', error)
      return null
    }
  }

  // Sign in function
  const signIn = async (
    credentials: LoginCredentials
  ): Promise<AuthResponse> => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'CLEAR_ERROR' })

    try {
      const { email, password } = credentials

      if (!email || !password) {
        const errorMessage = 'Email y contraseña son requeridos'
        dispatch({ type: 'SET_ERROR', payload: errorMessage })
        throw new Error(errorMessage)
      }

      let response = await amplifySignIn({ username: email, password })

      // Handle new password required
      if (
        response.nextStep?.signInStep ===
        'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        // In a real implementation, you might want to handle this differently
        // For now, we'll use the same password as the new password
        const { confirmSignIn } = await import('aws-amplify/auth')
        response = await confirmSignIn({
          challengeResponse: password,
        })
      }

      if (response.isSignedIn) {
        const user = await generateUser()
        dispatch({ type: 'SET_USER', payload: user })

        // Set user permissions
        if (user) {
          const permissions = createUserPermissions(user)
          dispatch({ type: 'SET_PERMISSIONS', payload: permissions })
        }
      }

      return response
    } catch (error: any) {
      console.error('Sign in error:', error)
      let errorMessage = 'Error al iniciar sesión'

      // Handle different types of authentication errors
      if (error.name === 'UserAlreadyAuthenticatedException') {
        // User is already authenticated, get user info
        const user = await generateUser()
        dispatch({ type: 'SET_USER', payload: user })

        // Set user permissions
        if (user) {
          const permissions = createUserPermissions(user)
          dispatch({ type: 'SET_PERMISSIONS', payload: permissions })
        }

        return { isSignedIn: true }
      } else if (error.name === 'NotAuthorizedException') {
        errorMessage = 'Usuario y/o contraseña incorrectos'
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = 'Usuario no encontrado'
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = 'Usuario no confirmado. Verifique su email.'
      } else if (error.name === 'PasswordResetRequiredException') {
        errorMessage = 'Debe restablecer su contraseña'
      } else if (error.name === 'TooManyRequestsException') {
        errorMessage = 'Demasiados intentos. Intente más tarde.'
      } else if (error.name === 'LimitExceededException') {
        errorMessage = 'Límite de intentos excedido. Intente más tarde.'
      } else if (error.message === 'Incorrect username or password.') {
        errorMessage = 'Usuario y/o contraseña incorrectos'
      } else if (error.message === 'User does not exist.') {
        errorMessage = 'Usuario no encontrado'
      } else if (error.message) {
        // Use the original error message if it's user-friendly
        if (
          error.message.includes('password') ||
          error.message.includes('username')
        ) {
          errorMessage = 'Usuario y/o contraseña incorrectos'
        } else {
          errorMessage = error.message
        }
      }

      dispatch({ type: 'SET_ERROR', payload: errorMessage })
      throw new Error(errorMessage)
    }
  }

  // Sign out function
  const signOut = async (): Promise<void> => {
    try {
      await amplifySignOut()
      dispatch({ type: 'SIGN_OUT' })
    } catch (error) {
      console.error('Error signing out:', error)
      // Even if sign out fails, clear local state
      dispatch({ type: 'SIGN_OUT' })
    }
  }

  // Clear error function
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  // Permission checking methods
  const hasPermission = (
    resource: PermissionResource,
    action: PermissionAction
  ): boolean => {
    if (!state.userPermissions) {
      return false
    }
    return state.userPermissions.hasPermission(resource, action)
  }

  const hasRole = (role: UserRole): boolean => {
    if (!state.user?.role) {
      return false
    }
    return state.user.role === role
  }

  // Refresh permissions based on current user
  const refreshPermissions = async (): Promise<void> => {
    if (state.user) {
      const permissions = createUserPermissions(state.user)
      dispatch({ type: 'SET_PERMISSIONS', payload: permissions })
    } else {
      dispatch({ type: 'SET_PERMISSIONS', payload: null })
    }
  }

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const user = await getCurrentUser()
        dispatch({ type: 'SET_USER', payload: user })

        // Set user permissions
        if (user) {
          const permissions = createUserPermissions(user)
          dispatch({ type: 'SET_PERMISSIONS', payload: permissions })
        }
      } catch (error: any) {
        // Don't log expected authentication errors
        if (
          error?.name !== 'UserUnAuthenticatedException' &&
          error?.name !== 'NotAuthorizedException'
        ) {
          console.error('Error checking auth status:', error)
        }
        dispatch({ type: 'SET_USER', payload: null })
        dispatch({ type: 'SET_PERMISSIONS', payload: null })
      }
    }

    checkAuthStatus()
  }, [])

  const value: AuthContextType = {
    ...state,
    signIn,
    signOut,
    getCurrentUser,
    clearError,
    hasPermission,
    hasRole,
    refreshPermissions,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
